// @ts-strict-ignore
import reduce from 'lodash/reduce';
import { snakeToLower } from '~/helpers/formatting';


const searchCoercionStrategy = [
  {
    key: 'entity_token',
    regex: /^[PBEG]-[A-Za-z0-9]{20,32}$/,
  },
  {
    key: 'evaluation_token',
    regex: /^[LS]-[A-Za-z0-9]{20,32}$/,
  },
  {
    key: 'ssn',
    regex: /^(\d{9}|\d{3}-\d{2}-\d{4})$/,
  },
  {
    key: 'ssn_last_4',
    regex: /^\d{4}$/,
  },
  {
    key: 'email',
    regex: /^.+@.+\..+$/,
  },
  {
    key: 'ip_address_v4',
    regex: /(([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])/,
  },
  {
    key: 'ip_address_v6',
    regex: /((([0-9a-fA-F]){1,4}):){7}([0-9a-fA-F]){1,4}/,
  },
  {
    key: 'external_entity_identifier',
    regex: /[\d-_:|]/,
  },
  {
    key: 'phone',
    regex: /((?:\+|00)[17](?: |-)?|(?:\+|00)[1-9]\d{0,2}(?: |-)?|(?:\+|00)1-\d{3}(?: |-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |-)[0-9]{3}(?: |-)[0-9]{4})|([0-9]{7}))/,
  },
];


export const coerceSearchInput = (inputValue, searchOptions) => {
  if (!searchOptions || !searchOptions.length) {
    return undefined;
  }
  const strategy = searchCoercionStrategy.find(option => (
    searchOptions.find(opt => opt.key === option.key) && inputValue.match(option.regex)
  ));
  if (strategy) {
    return strategy.key;
  }
  if (searchOptions.find(opt => opt.key === 'full_name')) {
    return 'full_name';
  }
  return searchOptions[0].key;
};


export const createSearchByOption = (key, value, label = '') => {
  let optionLabel = label;
  if (key === 'all') optionLabel = 'Search';
  return {
    key: 'search',
    value: { [key]: value },
    option_label: optionLabel,
    label: value,
  };
};


export const createSingleFieldSearchByOption = (key, label, value) => ({
  isSearch: true,
  label,
  key,
  value,
});


// If a search input string contains a semi-colon, parse the string as a
// search key and value. Else, parse it as just a value.
export const parseSearchString = (searchInput, searchOptions) => {
  const searchValue = searchInput.slice();
  if (searchValue.includes(':')) {
    // Split search input into maximum of 2 separate strings: key and value
    const [key, value] = searchValue.split(':', 2).map(str => str.trim());
    const searchLabels = searchOptions.map(option => option.label.toLowerCase());
    if (searchLabels.includes(snakeToLower(key))) {
      return [key, value];
    }
  }
  // If no semi-colon or string before semi-colon doesn't match an offered search label,
  // entire string is assumed as search value.
  return ['', searchValue.trim()];
};


export const getSearchInputIndex = (searchKey, searchValue, label) => (
  // Returns index of search key inside passed label
  snakeToLower(label).indexOf(snakeToLower(searchKey || searchValue))
);

export const permissionsFilters = {
  dob: 'READ:Personally identifiable information (PII):view_full_dob',
  ssn: 'READ:Personally identifiable information (PII):view_full_ssn',
  ssn_last_4: 'READ:Personally identifiable information (PII):view_partial_ssn',
};

export function createSearchOptions(inputValue, permissions, facet) {
  const options = reduce(
    permissionsFilters,
    (allOptions, permissionString, permissionKey) => (
      // If the user does not have permissions then filter out the option
      !permissions[permissionString]
        ? allOptions.filter(option => option.key !== permissionKey)
        : allOptions
    ),
    [...facet.options],
  );

  const [searchKey, searchValue] = parseSearchString(inputValue, options);
  const key = snakeToLower(searchKey);

  const coercedKey = coerceSearchInput(searchValue, options);
  const coercionMatters = !key || (coercedKey && coercedKey !== options[0].key);

  options.sort((optionA, optionB) => {
    const searchIsA = coercionMatters
      ? coercedKey === optionA.key
      : key === snakeToLower(optionA.label);
    const searchIndexA = getSearchInputIndex(searchKey, searchValue, optionA.label);
    const searchIsB = coercionMatters
      ? coercedKey === optionB.key
      : key === snakeToLower(optionB.label);
    const searchIndexB = getSearchInputIndex(searchKey, searchValue, optionB.label);
    if (searchIsA || (searchIndexA > -1 && searchIndexB === -1)) return -1;
    if (searchIsB || (searchIndexA === -1 && searchIndexB > -1)) return 1;
    return 0;
  });

  return {
    ...facet,
    options: options.map(option => createSingleFieldSearchByOption(
      option.key, option.label, searchValue,
    )),
  };
}


// eslint-disable-next-line no-underscore-dangle
export const filterOptions = (candidate, input) => candidate.data.__isNew__
  || (input && candidate.data.key && candidate.data.isSearch)
  || (candidate && candidate.label && candidate.label.toLowerCase().includes(input))
  || false;

/**
 * Determines whether a given option should be marked as 'selected' or not.
 * selectedVals represents the options selected for a given filter/facet
 * optionValue is one of those given options
 *
 * This functionality was ported over from DropdownFacet.jsx. At the time,
 * the context behind the conditions was not commented so the original logic
 * is left as is for now.
 *
 * Note: The values for the Version facet are number; rest currently are strings
 */
export const isDropdownOptionSelected = (
  defaultVal: string | null,
  selectedVals: (string | number)[],
  optionValue: string | number,
): boolean => {
  const isSelected = selectedVals.includes(optionValue)
    || selectedVals.includes(String(optionValue))
    || (selectedVals[0] === null && optionValue === defaultVal);
  return isSelected;
};
